import React, { useEffect, useState } from 'react';
import Config from '../../../helpers/Config';
import { translateText } from '../../../helpers/LanguageHelper';
import './CustomerNotificationBanner.css';

const STORAGE_KEY = 'ukl-customer-notifications-dismissed-v1';
const REFRESH_MS = 30000;
const permissionGranted = () => 'Notification' in window && window.Notification.permission === 'granted';
const keyFor = item => `${item.id}:${item.updatedUtc}`;
const storedDismissals = () => {
    try {
        const values = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || '[]');
        return Array.isArray(values) ? values.filter(value => typeof value === 'string').slice(-200) : [];
    } catch { return []; }
};
const safeLink = value => {
    if (typeof value !== 'string') return null;
    try { const url = new URL(value); return url.protocol === 'https:' && !url.username && !url.password ? url.href : null; }
    catch { return null; }
};

export default function CustomerNotificationBanner() {
    const [notificationsEnabled, setNotificationsEnabled] = useState(permissionGranted);
    const [items, setItems] = useState([]);
    const [dismissed, setDismissed] = useState(storedDismissals);
    const [now, setNow] = useState(Date.now);

    useEffect(() => {
        const update = () => setNotificationsEnabled(permissionGranted());
        window.addEventListener('ukl-notifications-enabled', update);
        window.addEventListener('focus', update);
        document.addEventListener('visibilitychange', update);
        return () => {
            window.removeEventListener('ukl-notifications-enabled', update);
            window.removeEventListener('focus', update);
            document.removeEventListener('visibilitychange', update);
        };
    }, []);

    useEffect(() => {
        if (notificationsEnabled) return undefined;
        let disposed = false, loading = false, request = null;
        const load = async () => {
            if (loading || disposed) return;
            loading = true;
            request = new window.AbortController();
            const timeout = window.setTimeout(() => request?.abort(), 10000);
            try {
                const url = new URL('api/customer-notifications', Config.ADMIN_BASE_URL);
                const response = await window.fetch(url.toString(), {
                    signal: request.signal, cache: 'no-store', credentials: 'omit', headers: { Accept: 'application/json' }
                });
                if (!response.ok) throw new Error('Notification feed unavailable');
                const data = await response.json();
                if (!Array.isArray(data.items)) throw new Error('Invalid notification feed');
                const fresh = data.items.filter(item => item && Number.isInteger(item.id) && item.id > 0
                    && typeof item.title === 'string' && item.title.trim()
                    && typeof item.message === 'string' && item.message.trim()
                    && typeof item.updatedUtc === 'string' && Number.isFinite(Date.parse(item.updatedUtc))
                    && (!item.endUtc || Number.isFinite(Date.parse(item.endUtc))));
                if (!disposed) { setItems(fresh); setNow(Date.now()); }
            } catch {
                // A failed feed must never interrupt shopping or keep displaying an old offer.
                if (!disposed) setItems([]);
            } finally {
                window.clearTimeout(timeout); request = null; loading = false;
            }
        };
        const resume = () => { setNow(Date.now()); if (!document.hidden) load(); };
        const timer = window.setInterval(resume, REFRESH_MS);
        document.addEventListener('visibilitychange', resume);
        window.addEventListener('focus', resume);
        load();
        return () => {
            disposed = true; request?.abort(); window.clearInterval(timer);
            document.removeEventListener('visibilitychange', resume); window.removeEventListener('focus', resume);
        };
    }, [notificationsEnabled]);

    // Expire the banner at the saved deadline even between successful feed refreshes.
    useEffect(() => {
        const deadlines = items.map(item => Date.parse(item.endUtc)).filter(time => Number.isFinite(time) && time > now);
        if (!deadlines.length) return undefined;
        const delay = Math.min(2147483647, Math.max(1, Math.min(...deadlines) - Date.now() + 1));
        const timer = window.setTimeout(() => setNow(Date.now()), delay);
        return () => window.clearTimeout(timer);
    }, [items, now]);

    // Show only the newest live notice. Closing it hides the bar, rather than revealing older notices.
    const current = items.find(item => !item.endUtc || Date.parse(item.endUtc) > now);
    const currentKey = current ? keyFor(current) : null;
    if (notificationsEnabled || !current || dismissed.includes(currentKey)) return null;

    const link = safeLink(current.linkUrl);
    const dismiss = () => {
        const next = [...dismissed.filter(key => key !== currentKey), currentKey].slice(-200);
        setDismissed(next);
        try { window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* Session storage may be unavailable. */ }
    };
    return (
        <section className="ukl-notification-banner" aria-label={translateText('Customer notification')}>
            <div className="ukl-notification-banner__inner">
                <p className="ukl-notification-banner__content" aria-live="polite" aria-atomic="true">
                    <strong className="ukl-notification-banner__title">{translateText(current.title)}</strong>
                    <span className="ukl-notification-banner__message">{translateText(current.message)}</span>
                    {link && current.linkText && <a className="ukl-notification-banner__link" href={link} rel="noopener noreferrer">{translateText(current.linkText)} <span aria-hidden="true">→</span></a>}
                </p>
                <button className="ukl-notification-banner__dismiss" type="button" aria-label={translateText('Dismiss notification')} onClick={dismiss}>×</button>
            </div>
        </section>
    );
}
